import type { FC, ReactNode } from 'react'

import Lenis from 'lenis'
import {
    createContext,
    useContext,
    useEffect,
    useState
} from 'react'
import { gsap, ScrollTrigger, prefersReducedMotion } from '@/lib/gsap'

interface SmoothScrollProps {
    children: ReactNode
}

const LenisContext = createContext<Lenis | null>(null)

export const useLenis = (): Lenis | null => useContext(LenisContext)

export const SmoothScroll: FC<SmoothScrollProps> = ({ children }): ReactNode => {
    const [lenis, setLenis] = useState<Lenis | null>(null)

    useEffect(() => {
        if (prefersReducedMotion()) return

        const instance = new Lenis({
            duration: 1.15,
            easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            smoothWheel: true
        })

        instance.on('scroll', ScrollTrigger.update)

        const tick = (time: number): void => {
            instance.raf(time * 1000)
        }

        gsap.ticker.add(tick)
        gsap.ticker.lagSmoothing(0)
        setLenis(instance)

        return () => {
            gsap.ticker.remove(tick)
            instance.destroy()
            setLenis(null)
        }
    }, [])

    return (
        <LenisContext.Provider value={lenis}>
            {children}
        </LenisContext.Provider>
    )
}
